/**
 * "Needs attention" gadget on the project Home: the services that crashed or whose build failed, each with a way to
 * read its log, and a line while quality checks block Publish (the release gate, hooks/useQualityGate.js) with a link
 * to Quality. "Ask the agent" on each line only where chat is on for this person (`onAskAgent`, null otherwise); the
 * log and Quality links only where the plan shows those tabs (null otherwise). Nothing wrong: one calm line.
 * `items`: `{ serviceName, service }` for each service of the project.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { serviceState } from '../../services/SandboxService';
import { shortServiceName } from '../../utils/gitRepoSource';
import useQualityGate, { blockedCount } from '../../hooks/useQualityGate';

const PROBLEMS = { crashed: 'Crashed', failed: 'Build failed' };

export const attentionOf = (items = []) => items
  .map(({ serviceName, service }) => ({ serviceName, state: serviceState(service) }))
  .filter(({ state }) => PROBLEMS[state]);

/** "1 quality check blocks publishing", "2 quality checks block publishing (missing: No tests)". */
export const blockedText = (count, missing = []) => `${count} quality ${count === 1 ? 'check blocks' : 'checks block'} publishing${missing.length ? ` (missing: ${missing.join(', ')})` : ''}`;

/** The gaps of the failing rules: "No tests", "No coverage report" (a missing check never counts as passing). */
export const missingLabels = (gate) => [...new Set((gate?.rules || []).filter((r) => r.status === 'fail' && r.missing).map((r) => r.gap).filter(Boolean))];

/** What the agent is asked for a service that crashed or failed to build, or for the checks that block Publish. */
export const askText = (problem) => (problem.blocked
  ? `${blockedText(problem.blocked, problem.missing)}. Look at the failing checks in Quality and fix what blocks Publish${problem.missing?.length ? ' (add the missing tests or coverage)' : ''}.`
  : `${shortServiceName(problem.serviceName)}: ${PROBLEMS[problem.state].toLowerCase()}. Read its log and fix it.`);

export default function NeedsAttention({ items = [], projectId = null, onOpenLogs = null, onOpenQuality = null, onAskAgent = null }) {
  const problems = attentionOf(items);
  const { gate } = useQualityGate(projectId);
  const blocked = projectId ? blockedCount(gate) : 0;
  const count = problems.length + (blocked > 0 ? 1 : 0);
  const ask = (problem) => onAskAgent && (
    <button className="ph-link" onClick={() => onAskAgent(askText(problem))} type="button">Ask the agent</button>
  );
  return (
    <div className="ph-card" data-testid="needs-attention">
      <div className="ph-card-head">
        <h2 className="ph-card-title">Needs attention</h2>
        {count > 0 && <span className="sbx-pill sbx-pill-failed">{count}</span>}
      </div>
      {count === 0 ? (
        <p className="ph-quiet">Nothing needs you. Every service is fine.</p>
      ) : (
        <ul className="ph-list">
          {problems.map((problem) => (
            <li className="ph-row" data-testid={`attention-${problem.serviceName}`} key={problem.serviceName}>
              <strong>{shortServiceName(problem.serviceName)}</strong>
              <span>{PROBLEMS[problem.state]}</span>
              {ask(problem)}
              {onOpenLogs && <button className="ph-link" onClick={() => onOpenLogs(problem.serviceName)} type="button">Read the log</button>}
            </li>
          ))}
          {blocked > 0 && (
            <li className="ph-row" data-testid="attention-quality">
              <strong>{blockedText(blocked, missingLabels(gate))}</strong>
              {ask({ blocked, missing: missingLabels(gate) })}
              {onOpenQuality && <button className="ph-link" onClick={onOpenQuality} type="button">Verify › Quality</button>}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

NeedsAttention.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ serviceName: PropTypes.string, service: PropTypes.object })),
  // (text) => void: the project chat, asked to fix it; only where chat is on for this person.
  onAskAgent: PropTypes.func,
  onOpenLogs: PropTypes.func,
  onOpenQuality: PropTypes.func,
  // The release gate's project (useQualityGate); none: no quality line.
  projectId: PropTypes.string,
};
